import { Skeleton, Stack, Typography } from "@mui/material";
import type { ReactNodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import React from "react";
import { useTheme } from "@mui/material";
import { CircularLoader } from "components/loader";

export default (props: ReactNodeViewProps<HTMLLabelElement>) => {
  const theme = useTheme();
  const { url, title, description, src, isLoading } = props.node.attrs;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    e.stopPropagation();
  };

  return (
    <NodeViewWrapper className="link-card">
      <Stack
        direction={"row"}
        onClick={handleClick}
        sx={{
          p: "8px",
          borderRadius: "8px",
          alignItems: "center",
          gap: "8px",
          backgroundColor: theme.palette.primitive.system.violet[50],
          border: `1px solid ${theme.palette.primitive.system.violet[100]}`,
          cursor: url ? "pointer" : "default",
          transition: "all 0.2s ease",
          "&:hover": url
            ? {
                backgroundColor: theme.palette.primitive.system.violet[50],
              }
            : {},
        }}
      >
        {isLoading ? (
          <Stack>
            <CircularLoader
              size="16px"
              sx={{
                "&.MuiCircularProgress-root": {
                  color: theme.palette.lightBg.high,
                },
              }}
            />
          </Stack>
        ) : (
          <Stack direction={"row"} sx={{ gap: "8px" }}>
            {src && (
              <img
                src={src}
                alt=""
                width={"88px"}
                height={"64px"}
                style={{
                  objectFit: "cover",
                  borderRadius: "4px",
                  flexShrink: 0,
                  backgroundColor: theme.palette.primitive.system.violet[100],
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
            <Stack
              sx={{
                flex: 1,
                minWidth: 0,
                gap: "4px",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.lightBg.high,
                  textDecoration: url ? "none" : "inherit",
                  "&:hover": url
                    ? {
                        textDecoration: "underline",
                      }
                    : {},
                }}
              >
                {title || "Untitled"}
              </Typography>

              <Typography
                variant="body-small"
                sx={{
                  color: theme.palette.lightBg.medium,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {description || "No description available"}
              </Typography>
              {url && (
                <Typography
                  variant="link-small"
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                >
                  {new URL(url).hostname}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </NodeViewWrapper>
  );
};
